import { relativeTime } from '../../lib/format.js'
import { dateTimeText, fullDate } from '../../lib/time.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mail-team-log ---
let __fez_component_0;
{
// Written as `icon: '...'` so `bun run icons` bundles them.
const KINDS = [
  { kind: 'assigned', icon: 'user-circle' },
  { kind: 'unassigned', icon: 'user-circle-minus' },
  { kind: 'done', icon: 'check-circle' },
  { kind: 'reopened', icon: 'arrow-counter-clockwise' },
  { kind: 'moved', icon: 'folder-simple' },
  { kind: 'replied', icon: 'arrow-bend-up-left' },
  { kind: 'snoozed', icon: 'clock' },
  { kind: 'woke', icon: 'alarm' },
  { kind: 'category', icon: 'funnel' },
]
const FOLDERS = { inbox: 'the inbox', archive: 'the archive', spam: 'spam', trash: 'the trash', sent: 'sent' }
const CATEGORIES = { inbox: 'the Inbox', feed: 'the Feed', papertrail: 'the Paper Trail', spam: 'Spam' }
__fez_component_0 = class {
  PROPS = {
    container: { type: String, required: true },
    mailbox: String,
    team: Object,
    people: Array,
    on_team: { type: Function, state: true, default: () => {} },
  }

  init() {
    this.state.saving = false
    this.state.error = ''
  }

  name(actor) {
    if (actor === 'mail') return 'New mail'
    if (actor === 'snooze') return 'Snooze'
    if (actor === this.globalState.me?.email) return 'You'
    if (actor.startsWith('key:')) return 'The mailbox key'
    return this.props.people.find((person) => person.email === actor)?.name || actor
  }

  line(event) {
    const who = this.name(event.actor)
    switch (event.kind) {
      case 'assigned': return `${who} assigned it to ${event.body === this.globalState.me?.email ? 'you' : this.name(event.body)}`
      case 'unassigned': return `${who} unassigned it`
      case 'done': return `${who} marked it done`
      case 'reopened': return event.actor === 'mail' ? 'New mail reopened it' : `${who} reopened it`
      case 'moved': return `${who} moved it to ${FOLDERS[event.body] || event.body}`
      case 'replied': return `${who} replied`
      case 'snoozed': return `${who} snoozed it until ${dateTimeText(event.body, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
      case 'woke': return event.actor === 'snooze' ? 'The snooze ended' : `${who} woke it`
      case 'category':
        if (event.actor === 'system') return `Writing to the sender let it into ${CATEGORIES[event.body] || event.body}`
        return `${who} moved the sender to ${CATEGORIES[event.body] || event.body}`
      default: return who
    }
  }

  async addNote(event) {
    event.preventDefault()
    const body = this.state.noteInput.value.trim()
    if (!body) return
    this.state.saving = true
    this.state.error = ''
    try {
      const view = await window.App.api(`container/${this.props.container}/thread_note`, { thread: this.props.team.thread, mailbox: this.props.mailbox || undefined, body }, { silent: true })
      this.state.noteInput.value = ''
      this.state.on_team(view)
    } catch (error) {
      this.state.error = error.details?.body || error.message
    } finally {
      this.state.saving = false
    }
  }

  noteKey(event) {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) this.addNote(event)
  }

  beforeRender() {
    const team = this.props.team
    this.state.ready = Boolean(team)
    this.state.entries = (team?.activity || []).map((event, index) => ({
      key: `${index}-${event.created_at}`,
      note: event.kind === 'note',
      who: this.name(event.actor),
      text: event.kind === 'note' ? event.body : this.line(event),
      when: relativeTime(event.created_at),
      full: fullDate(event.created_at),
      icon: KINDS.find((item) => item.kind === event.kind)?.icon || 'user-circle',
    }))
  }

  CSS = `:fez {
display:block;.team-log{display:grid;gap:var(--space-2);margin-top:var(--space-5)}.team-title{color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-bold);letter-spacing:0.06em;text-transform:uppercase}.team-line{display:flex;align-items:center;gap:var(--space-2);color:var(--color-text-muted);font-size:var(--text-sm)}.team-line-icon{display:flex;color:var(--color-text-subtle)}.team-when{color:var(--color-text-subtle);font-size:var(--text-xs)}.team-note{padding:var(--space-3) var(--space-4);border:1px solid var(--color-warning-soft);border-left:3px solid var(--color-warning);border-radius:var(--radius-md);background:var(--color-warning-soft)}.team-note-head{display:flex;align-items:baseline;gap:var(--space-2);margin-bottom:var(--space-1);font-size:var(--text-sm);font-weight:var(--weight-semibold)}.team-note-body{white-space:pre-wrap;overflow-wrap:anywhere}.team-form{display:grid;gap:var(--space-2)}.team-input{width:100%;min-height:64px;padding:var(--space-2) var(--space-3);border:1px solid var(--color-border-strong);border-radius:var(--radius-md);background:var(--color-bg-raised);color:var(--color-text);font:inherit;resize:vertical;&:focus{border-color:var(--color-accent);outline:0}}.team-actions{display:flex;align-items:center;justify-content:space-between;gap:var(--space-3)}.team-hint{color:var(--color-text-subtle);font-size:var(--text-xs)}.team-error{color:var(--color-danger);font-size:var(--text-xs)}
}`

  HTML = `{#if state.ready}
<section class="team-log" aria-label="Notes and activity">
<h2 class="team-title">Notes and activity</h2>
{#each state.entries as entry}
{#if entry.note}
<div class="team-note" key={entry.key}>
<div class="team-note-head">{entry.who}<span class="team-when" title={entry.full}>{entry.when}</span></div>
<div class="team-note-body">{entry.text}</div>
</div>
{:else}
<div class="team-line" key={entry.key}>
<span class="team-line-icon"><ui-icon name={entry.icon} size="14"></ui-icon></span>
<span>{entry.text}</span>
<span class="team-when" title={entry.full}>{entry.when}</span>
</div>
{/if}
{/each}
<form class="team-form" onsubmit="fez.addNote(event)">
<textarea class="team-input" placeholder="Add an internal note: only people who read this mailbox see it" aria-label="Internal note" fez:this="noteInput" onkeydown="fez.noteKey(event)"></textarea>
<div class="team-actions">
{#if state.error}<span class="team-error">{state.error}</span>{:else}<span class="team-hint">Never emailed. Ctrl+Enter adds it.</span>{/if}
<ui-button type="submit" size="sm" icon="note-pencil" label="Add note" :loading="state.saving"></ui-button>
</div>
</form>
</section>
{/if}`
};
}

Fez('mail-team-log', __fez_component_0);

export default __fez_component_0;
